import { useEffect, useRef } from "react";
import type { UseFormReturnType } from "@mantine/form";
import { ReferralServiceNeedsFields } from "@/components/domain/care-plan/ReferralServiceNeedsFields";
import {
  createEmptyServiceNeedFormItem,
  type ServiceNeedFormItem,
} from "@benchmark/core/types";

/**
 * Form-shape contract required by {@link ReferralsSection}. Any form
 * that owns the referral fields below — both intake form data shapes do
 * via `CompleteIntakeChainFields` — can be passed in without casts.
 *
 * @agentic connections
 */
export type ReferralsCapableFormShape = {
  "followup-service-needs"?: ServiceNeedFormItem[];
  "followup-requires-approval"?: boolean;
  "followup-flags"?: string[];
};

/**
 * Props for the ReferralsSection.
 * @agentic connections
 */
export type ReferralsSectionProps<T extends ReferralsCapableFormShape> = {
  form: UseFormReturnType<T>;
};

/**
 * Referrals step rendered when triage or CoCM program is selected.
 * Seeds one empty service need and defaults approval to true on first render.
 * @agentic connections
 */
export const ReferralsSection = <T extends ReferralsCapableFormShape>({
  form,
}: ReferralsSectionProps<T>) => {
  const seededRef = useRef(false);

  const serviceNeeds = form.values["followup-service-needs"] ?? [];
  const requiresApproval = form.values["followup-requires-approval"] ?? true;

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;

    if (serviceNeeds.length === 0) {
      form.setFieldValue(
        "followup-service-needs" as never,
        [createEmptyServiceNeedFormItem()] as never,
      );
    }
    if (form.values["followup-requires-approval"] === false) {
      form.setFieldValue("followup-requires-approval" as never, true as never);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const followupFlags = form.values["followup-flags"] ?? [];
  const clinicianContactOnly = followupFlags.includes("clinician-contact-only");

  const handleClinicianContactOnlyChange = (checked: boolean) => {
    const current = form.values["followup-flags"] ?? [];
    if (checked) {
      if (!current.includes("clinician-contact-only")) {
        form.setFieldValue(
          "followup-flags" as never,
          [...current, "clinician-contact-only"] as never,
        );
      }
    } else {
      form.setFieldValue(
        "followup-flags" as never,
        current.filter((code) => code !== "clinician-contact-only") as never,
      );
    }
  };

  return (
    <ReferralServiceNeedsFields
      serviceNeeds={serviceNeeds}
      requiresApproval={requiresApproval}
      serviceNeedsError={
        form.errors["followup-service-needs"]
          ? String(form.errors["followup-service-needs"])
          : null
      }
      onServiceNeedsChange={(items) =>
        form.setFieldValue("followup-service-needs" as never, items as never)
      }
      onRequiresApprovalChange={(checked) =>
        form.setFieldValue(
          "followup-requires-approval" as never,
          checked as never,
        )
      }
      clinicianContactOnly={clinicianContactOnly}
      onClinicianContactOnlyChange={handleClinicianContactOnlyChange}
    />
  );
};
